/**
 * The black top bar: the Gibs logo and a small uppercase wordmark, a
 * "Search docs" box, small-caps text links to the docs, and
 * the theme switch. The layout and the colours follow the docs site's bar, so
 * a reader who moves between the two sees one frame.
 *
 * The docs links and the search box wait for the signed release list: the docs
 * live inside the release, so each link points at that release's own
 * content-address origin once the list verifies. See `verifiedLinks.ts`.
 */
import { createElement, createTextElement } from './dom'
import { THEME_ACTION_ATTRIBUTE } from './theme'
import { RELEASE_PATH_ATTRIBUTE, VERIFIED_LINK_ATTRIBUTE } from './verifiedLinks'

/** The docs home, where the search lives: a path under the verified release's root. */
export const DOCS_PATH = 'docs/'

/** The guide for people who bridge: a path under the verified release's root. */
export const GUIDE_PATH = 'docs/guide/'

/**
 * A link into the release's own files. It has no address until the signed
 * list verifies and `pointReleaseLinks` fills it in.
 */
const createReleaseLink = (path: string, text: string, className: string): HTMLAnchorElement => {
  const link = createTextElement('a', text, className)
  link.setAttribute(RELEASE_PATH_ATTRIBUTE, path)
  return link
}

/** Hides an element that links into the release until the signed list verifies. */
const waitForVerifiedList = (element: HTMLElement): void => {
  element.toggleAttribute(VERIFIED_LINK_ATTRIBUTE, true)
  element.hidden = true
}

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/**
 * The key hint for the search box: the Command symbol on an Apple computer,
 * "Ctrl K" elsewhere. `platform` is `navigator.platform`.
 */
export const searchShortcutLabel = (platform: string): string =>
  /mac|iphone|ipad|ipod/i.test(platform) ? '⌘K' : 'Ctrl K'

/**
 * Whether a key press asks for the search. Command K on an Apple computer and
 * Control K elsewhere both count. Either one counts on any computer, since a
 * reader may use an external keyboard.
 */
export const isSearchShortcut = (event: {
  readonly key: string
  readonly metaKey: boolean
  readonly ctrlKey: boolean
}): boolean => event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)

const createSearchIcon = (): SVGSVGElement => {
  const icon = document.createElementNS(SVG_NAMESPACE, 'svg')
  icon.setAttribute('class', 'top-bar-search-icon')
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('width', '1em')
  icon.setAttribute('height', '1em')
  icon.setAttribute('fill', 'none')
  icon.setAttribute('stroke', 'currentColor')
  icon.setAttribute('stroke-width', '2')
  icon.setAttribute('stroke-linecap', 'round')
  icon.setAttribute('stroke-linejoin', 'round')
  icon.setAttribute('aria-hidden', 'true')
  const circle = document.createElementNS(SVG_NAMESPACE, 'circle')
  circle.setAttribute('cx', '11')
  circle.setAttribute('cy', '11')
  circle.setAttribute('r', '7')
  const handle = document.createElementNS(SVG_NAMESPACE, 'path')
  handle.setAttribute('d', 'm20 20-3.5-3.5')
  icon.append(circle, handle)
  return icon
}

/**
 * The search box. The docs search opens from the docs pages themselves and
 * takes no address parameter, so this is a link to the docs home.
 */
const createSearchLink = (platform: string): HTMLAnchorElement => {
  const link = createReleaseLink(DOCS_PATH, '', 'top-bar-search')
  link.setAttribute('aria-label', 'Search docs')
  link.append(
    createSearchIcon(),
    createTextElement('span', 'Search docs', 'top-bar-search-label'),
    createTextElement('kbd', searchShortcutLabel(platform), 'top-bar-search-key'),
  )
  waitForVerifiedList(link)
  return link
}

/** Builds the top bar. The theme buttons are the ones `theme.ts` wires up, by their data attributes. */
export const createTopBar = (options: { platform: string }): HTMLElement => {
  const bar = createElement('div', 'top-bar-inner')

  const brand = createElement('a', 'top-bar-brand')
  brand.href = './'
  const logo = createElement('img', 'top-bar-logo')
  logo.src = '/favicon.png'
  logo.alt = ''
  logo.width = 22
  logo.height = 22
  brand.append(logo, createTextElement('span', 'Gibs Finance', 'top-bar-wordmark'))

  const links = createElement('nav', 'top-bar-links')
  links.setAttribute('aria-label', 'Docs')
  links.append(
    createReleaseLink(DOCS_PATH, 'Docs', 'top-bar-link'),
    createReleaseLink(GUIDE_PATH, 'Guide', 'top-bar-link'),
  )
  waitForVerifiedList(links)

  const themeControl = createElement('div', 'theme-control')
  themeControl.setAttribute('role', 'group')
  themeControl.setAttribute('aria-label', 'Theme')
  const themeToggle = createElement('button', 'theme-button')
  themeToggle.type = 'button'
  themeToggle.setAttribute(THEME_ACTION_ATTRIBUTE, 'toggle')
  const systemButton = createElement('button', 'theme-button')
  systemButton.type = 'button'
  systemButton.setAttribute(THEME_ACTION_ATTRIBUTE, 'system')
  themeControl.append(themeToggle, systemButton)

  bar.append(brand, createSearchLink(options.platform), createElement('span', 'top-bar-spacer'), links, themeControl)
  return bar
}

/**
 * Fills the page's `#top-bar` element and wires the search shortcut. The
 * shortcut sends the reader to the docs home once the list has verified. Does nothing when the page has
 * no `#top-bar`, so the developer harness still works if it omits one.
 */
export const mountTopBar = (host: HTMLElement | null): void => {
  if (host === null) {
    return
  }
  host.replaceChildren(createTopBar({ platform: navigator.platform }))
  document.addEventListener('keydown', (event) => {
    if (!isSearchShortcut(event)) {
      return
    }
    // The docs wait for the verified list, like their links. No list, no jump.
    const search = host.querySelector<HTMLAnchorElement>('.top-bar-search')
    if (search === null || search.hidden || search.getAttribute('href') === null) {
      return
    }
    event.preventDefault()
    window.location.assign(search.href)
  })
}
